import Signature from './AbstractSignature';
import { CreateElement, VNode } from 'vue';
import { ChildParams, referencesMethod } from '@/templates/References';

export default function DartSignatureFactory(kind: string) {
  switch (kind) {
    case 'method':
      return DartMethodSignature;
    case 'prop':
      return DartPropertySignature;
    case 'typedef':
      return DartTypedefSignature;
    default:
      return {
        getSignature(_method: any): null {
          return null;
        },
      };
  }
}

class DartHelper extends Signature {
  public static getSignature(
    method: referencesMethod,
    isTypeDef?: boolean
  ): (h: CreateElement) => VNode {
    const paramSignatures = this.paramSignatures(method.params);
    const name = method.title;
    const returnsType = this.returnsType(method.returns);

    return (h) =>
      h('code', [
        isTypeDef ? h('span', 'typedef ') : null,
        returnsType(h),
        ` ${name}`,
        h('span', { class: 'token punctuation' }, '('),
        paramSignatures(h),
        h('span', { class: 'token punctuation' }, ')'),
      ]);
  }

  /**
   * Проходимся по массиву имен параметров,
   * каждому сопоставляем параметр из массива params и записываем в строку следующего вида:
   * deviceWithType:(VIAudioDeviceType)type
   *
   * @param paramSignatures массив содержащий параметры метода заданные в method.params
   * @return Function возвращает рендер функцию, которая создает часть сигнатуры
   */
  private static paramSignatures(paramSignatures: ChildParams[]): (h: CreateElement) => VNode {
    if (!paramSignatures) return (h) => h();

    return (h) =>
      h('span', [
        paramSignatures[0].properties?.named ? h('span', { class: 'token punctuation' }, '{') : '',
        paramSignatures.map((val: ChildParams, idx: number) => {
          return h('span', [
            this.checkOptional(val, true, h),
            val.types ? this.returnsType(val.types)(h) : '',
            ` ${val.title}`,
            this.checkOptional(val, false, h),
            !(idx === paramSignatures.length - 1)
              ? h('span', { class: 'token punctuation' }, ', ')
              : '',
          ]);
        }),
        paramSignatures[0].properties?.named ? h('span', { class: 'token punctuation' }, '}') : '',
      ]);
  }

  /**
   *
   *
   * @param paramSignature параметры метода
   * @param bracketStart - если true, то открывающая скобка, иначе закрывающая
   * @param h - функция для создания html элемента
   * @return Function
   */
  private static checkOptional(
    paramSignature: ChildParams,
    bracketStart: boolean,
    h: CreateElement
  ): VNode {
    if (paramSignature.properties?.defaultValue)
      return bracketStart
        ? null
        : h('span', [
            h('span', { class: 'token punctuation' }, ' = '),
            h(
              'span',
              { class: 'token md-parse type' },
              `${paramSignature.properties?.defaultValue}`
            ),
          ]);

    if (paramSignature.properties?.named || !paramSignature.properties?.optional) return null;

    return h('span', { class: 'token punctuation' }, bracketStart ? '[' : ']');
  }
}

class DartMethodSignature extends Signature {
  public static getSignature(method: referencesMethod): (h: CreateElement) => VNode {
    return DartHelper.getSignature(method, false);
  }
}

class DartPropertySignature extends Signature {
  public static getSignature(property: any): (h: CreateElement) => VNode {
    const title: string = property.title;
    const returnsType = this.returnsType(property.returns);

    return (h) => h('code', [returnsType(h), h('span', ` ${title}`)]);
  }
}

class DartTypedefSignature extends Signature {
  public static getSignature(typedef: referencesMethod): (h: CreateElement) => VNode {
    return DartHelper.getSignature(typedef, true);
  }
}
